import { ApolloError } from 'apollo-client'
import React, { useContext } from 'react'
import { Mutation, MutationFunction } from 'react-apollo'

import { PageContext } from 'main/components/common/Page'
import { AppContext, IAppContext } from 'main/framework'
import { NativeLink } from 'main/ui'
import { errorAlert, IPageAlert, successAlert } from 'main/util/page'
import { Plaid } from 'main/util/paymentMethod'

import {
  PAYMENT_METHODS_QUERY,
  UPSERT_PAYMENT_METHOD_MUTATION,
  UPSERT_PAYMENT_METHOD_PENDING_MUTATION,
  UpsertPaymentMethod,
  UpsertPaymentMethodPending,
  UpsertPaymentMethodPendingVariables,
  UpsertPaymentMethodVariables,
} from 'main/gql/paymentMethod'

interface IContainerProps {
  children: React.ReactNode
  onComplete?: () => void
}

export default function Container(props: IContainerProps) {
  const { children, onComplete } = props
  const { user } = useContext(AppContext)
  const { setAlert } = useContext(PageContext)

  const mutationProps = {
    onCompleted: () => {
      setAlert(successAlert)
      if (onComplete) onComplete()
    },
    onError: (error: ApolloError) => {
      const errorMessage = error.graphQLErrors[0] && error.graphQLErrors[0].message
      return errorMessage ? setAlert({ body: errorMessage, kind: 'danger' }) : setAlert(errorAlert)
    },
    refetchQueries: [{ query: PAYMENT_METHODS_QUERY, variables: { userId: user!.id } }],
  }

  return (
    <Mutation<UpsertPaymentMethod, UpsertPaymentMethodVariables>
      mutation={UPSERT_PAYMENT_METHOD_MUTATION}
      {...mutationProps}
    >
      {(upsertPaymentMethod, { loading: upsertingPaymentMethod }) => (
        <Mutation<UpsertPaymentMethodPending, UpsertPaymentMethodPendingVariables>
          mutation={UPSERT_PAYMENT_METHOD_PENDING_MUTATION}
          {...mutationProps}
        >
          {(upsertPaymentMethodPending, { loading: upsertingPaymentMethodPending }) => {
            return (
              <BankLink
                user={user!}
                loading={upsertingPaymentMethod || upsertingPaymentMethodPending}
                setAlert={setAlert}
                upsertPaymentMethod={upsertPaymentMethod}
                upsertPaymentMethodPending={upsertPaymentMethodPending}
              >
                {children}
              </BankLink>
            )
          }}
        </Mutation>
      )}
    </Mutation>
  )
}

interface IProps {
  user: IAppContext['user']
  loading: boolean
  setAlert: (alert: IPageAlert | null) => void
  upsertPaymentMethod: MutationFunction<UpsertPaymentMethod, UpsertPaymentMethodVariables>
  upsertPaymentMethodPending: MutationFunction<
    UpsertPaymentMethodPending,
    UpsertPaymentMethodPendingVariables
  >
  children: React.ReactNode
}

function BankLink(props: IProps) {
  const { user, loading, setAlert, upsertPaymentMethod, upsertPaymentMethodPending, children } =
    props

  return <NativeLink onClick={handleClick}>{loading ? 'Saving...' : children}</NativeLink>

  function handleClick() {
    Plaid.create(user!)
      .then((data: Plaid.ISuccessData) => {
        const account = data.metadata.account

        if (Plaid.isPendingVerification(account)) {
          upsertPaymentMethodPending({
            variables: toUpsertPaymentMethodPendingVariables(user, data),
          })
        } else {
          // @ts-ignore
          upsertPaymentMethod({ variables: toUpsertPaymentMethodVariables(data) })
        }
      })
      .catch(
        (error: Plaid.IErrorData | null) =>
          error && setAlert({ body: error.display_message, kind: 'danger' }),
      )
  }
}

function toUpsertPaymentMethodVariables(variables: Plaid.ISuccessData) {
  const toAccount = (account: Plaid.IAccount) => {
    return {
      id: account.id,
      mask: account.mask,
      name: account.name,
      subtype: account.subtype,
      type: account.type,
      verificationStatus: account.verification_status,
    }
  }

  const { metadata } = variables

  return {
    stripeToken: variables.stripeToken,
    publicToken: variables.publicToken,
    metadata: {
      account: toAccount(metadata.account),
      accountId: metadata.account_id,
      accounts: metadata.accounts.map(acc => toAccount(acc)),
      institution: {
        name: metadata.institution.name,
        institutionId: metadata.institution.institution_id,
      },
      linkSessionId: metadata.link_session_id,
      publicToken: metadata.public_token,
    },
  }
}

function toUpsertPaymentMethodPendingVariables(
  user: IAppContext['user'],
  variables: Plaid.ISuccessData,
) {
  const { publicToken, metadata } = variables
  const { account } = metadata

  return {
    paymentMethodPending: {
      accountLast4: account.mask,
      accountName: account.name,
      plaidAccountId: account.id,
      verificationType: Plaid.getVerificationType(account),
    },
    plaidPublicToken: publicToken,
    userId: user!.id,
  }
}
